import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Typography, CircularProgress, Paper, Tabs, Tab,
} from '@material-ui/core';
import { SERVER_NAME } from './config';

const SIGNAL_TYPE_LABELS = {
  capital_migration: 'Capital Migration',
  destination_prediction: 'Destination',
  narrative_acceleration: 'Narrative',
  liquidity_forecast: 'Liquidity',
  congestion_formation: 'Congestion',
  route_hazard: 'Hazard',
  route_closure: 'Route Closure',
  route_emergence: 'Route Emergence',
  agent_swarm_formation: 'Agent Swarm',
  capital_conviction: 'Conviction',
};

const SIGNAL_TYPE_COLORS = {
  capital_migration: '#4d79ff',
  destination_prediction: '#22c55e',
  narrative_acceleration: '#a78bfa',
  liquidity_forecast: '#38bdf8',
  congestion_formation: '#f59e0b',
  route_hazard: '#ef4444',
  route_closure: '#dc2626',
  route_emergence: '#10b981',
  agent_swarm_formation: '#8b5cf6',
  capital_conviction: '#06b6d4',
};

const REGIME_COLORS = {
  risk_on: '#22c55e',
  risk_off: '#ef4444',
  neutral: '#9ca3af',
  volatile: '#f59e0b',
};

const STANCE_COLORS = {
  risk_on: '#22c55e',
  risk_off: '#ef4444',
  neutral: '#9ca3af',
  cautious: '#f59e0b',
  crowded: '#8b5cf6',
};

const PREDICTION_STATUS_COLORS = {
  pending: '#6b7280',
  correct: '#22c55e',
  incorrect: '#ef4444',
  mixed: '#f59e0b',
  disputed: '#8b5cf6',
};

const DIRECTION_COLORS = {
  inflow: '#22c55e',
  outflow: '#ef4444',
  neutral: '#9ca3af',
  mixed: '#f59e0b',
};

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 40px',
    maxWidth: 1300,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: { padding: '10px 8px 24px' },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  title: {
    fontSize: '1.7rem',
    fontWeight: 900,
    letterSpacing: -0.5,
    color: '#e5e7ff',
    textShadow: '0 0 20px rgba(77,121,255,0.5)',
  },
  subtitle: {
    fontSize: 13,
    color: '#9ea4d8',
    marginTop: 2,
  },
  regimeBadge: {
    fontWeight: 800,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderRadius: 8,
    padding: '4px 12px',
    border: '1px solid',
  },
  tabs: {
    marginBottom: 20,
    borderBottom: '1px solid rgba(77,121,255,0.2)',
    minHeight: 40,
  },
  tab: {
    color: '#9ea4d8',
    fontWeight: 700,
    fontSize: 13,
    textTransform: 'none',
    letterSpacing: 0.3,
    minHeight: 40,
    '&.Mui-selected': { color: '#e5e7ff' },
  },
  tabIndicator: {
    backgroundColor: '#4d79ff',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  panel: {
    background: 'rgba(15,18,40,0.85)',
    border: '1px solid rgba(77,121,255,0.2)',
    borderRadius: 14,
    padding: '16px 18px',
  },
  panelTitle: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 12,
  },
  signalRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    '&:last-child': { borderBottom: 'none' },
  },
  signalBadge: {
    fontSize: 10,
    fontWeight: 800,
    padding: '2px 7px',
    borderRadius: 6,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    marginTop: 1,
  },
  signalAnswer: {
    fontSize: 13,
    color: '#d1d5db',
    lineHeight: 1.4,
    flex: 1,
  },
  signalMeta: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 3,
  },
  confidence: {
    fontSize: 11,
    fontWeight: 700,
    color: '#4d79ff',
    flexShrink: 0,
    marginTop: 1,
  },
  flowRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    fontSize: 13,
    '&:last-child': { borderBottom: 'none' },
  },
  flowOrigin: { color: '#9ea4d8', fontWeight: 600, minWidth: 80, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  flowArrow: { color: '#4d79ff', fontWeight: 900, fontSize: 16, flexShrink: 0 },
  flowDest: { color: '#e5e7ff', fontWeight: 700, flex: 1 },
  flowConf: { fontSize: 11, color: '#6b7280' },
  hazardRow: {
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    '&:last-child': { borderBottom: 'none' },
  },
  hazardLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: '#ef4444',
  },
  hazardSub: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 2,
  },
  fullPanel: {
    background: 'rgba(15,18,40,0.85)',
    border: '1px solid rgba(77,121,255,0.2)',
    borderRadius: 14,
    padding: '16px 18px',
    marginBottom: 16,
  },
  statRow: {
    display: 'flex',
    gap: 24,
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  stat: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: '1.4rem',
    fontWeight: 900,
    color: '#4d79ff',
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: 11,
    color: '#9ea4d8',
    marginTop: 2,
  },
  empty: {
    color: '#6b7280',
    fontSize: 13,
    padding: '12px 0',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  refreshNote: {
    fontSize: 11,
    color: '#6b7280',
    textAlign: 'right',
    marginTop: 8,
  },
  // News tab styles
  briefCard: {
    background: 'rgba(15,18,40,0.85)',
    border: '1px solid rgba(77,121,255,0.2)',
    borderRadius: 14,
    padding: '18px 20px',
    marginBottom: 14,
  },
  briefHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  briefHeadline: {
    fontSize: '1.05rem',
    fontWeight: 800,
    color: '#e5e7ff',
    lineHeight: 1.3,
    flex: 1,
  },
  stanceBadge: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderRadius: 8,
    padding: '3px 10px',
    border: '1px solid',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  briefSummary: {
    fontSize: 13,
    color: '#d1d5db',
    lineHeight: 1.55,
    marginBottom: 10,
  },
  briefTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  briefTag: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 6,
    background: 'rgba(77,121,255,0.15)',
    color: '#9ea4d8',
    border: '1px solid rgba(77,121,255,0.25)',
  },
  briefTimestamp: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 8,
  },
  // Predictions tab styles
  predRow: {
    padding: '12px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    '&:last-child': { borderBottom: 'none' },
  },
  predClaim: {
    fontSize: 13,
    color: '#e5e7ff',
    fontWeight: 600,
    lineHeight: 1.4,
    marginBottom: 6,
  },
  predMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  predBadge: {
    fontSize: 10,
    fontWeight: 800,
    padding: '2px 7px',
    borderRadius: 6,
    border: '1px solid',
  },
  notableRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    '&:last-child': { borderBottom: 'none' },
  },
  notabilityScore: {
    fontSize: 13,
    fontWeight: 900,
    color: '#4d79ff',
    minWidth: 32,
    flexShrink: 0,
    marginTop: 1,
  },
}));

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = Math.floor((Date.now() - new Date(isoStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtConf(v) {
  const n = Number(v);
  if (!n) return '';
  return n < 1 ? `${Math.round(n * 100)}%` : `${Math.round(n)}%`;
}

function fmtPct(v) {
  return `${Math.round(Number(v) * 100)}%`;
}

export default function MapsPage() {
  const classes = useStyles();
  const [tab, setTab] = useState(0);

  // Traffic tab state
  const [destinations, setDestinations] = useState([]);
  const [hazards, setHazards] = useState([]);
  const [congestion, setCongestion] = useState([]);
  const [signals, setSignals] = useState([]);
  const [graph, setGraph] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [state, setState] = useState(null);

  // News tab state
  const [briefs, setBriefs] = useState([]);

  // Predictions tab state
  const [watchPredictions, setWatchPredictions] = useState([]);
  const [notable, setNotable] = useState([]);

  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [
        destRes, hazRes, congRes, sigRes, graphRes, calRes, stateRes,
        newsRes, predsRes, notableRes,
      ] = await Promise.allSettled([
        axios.get(`${SERVER_NAME}/maps/destinations?limit=8`),
        axios.get(`${SERVER_NAME}/maps/hazards?limit=6`),
        axios.get(`${SERVER_NAME}/maps/congestion?limit=6`),
        axios.get(`${SERVER_NAME}/maps/signals?limit=20`),
        axios.get(`${SERVER_NAME}/maps/graph`),
        axios.get(`${SERVER_NAME}/maps/calibration`),
        axios.get(`${SERVER_NAME}/maps/state`),
        axios.get(`${SERVER_NAME}/maps/news/recent?limit=5`),
        axios.get(`${SERVER_NAME}/agents/predictions?limit=20`),
        axios.get(`${SERVER_NAME}/maps/notable?limit=10`),
      ]);
      if (destRes.status === 'fulfilled') setDestinations(destRes.value.data?.destinations || []);
      if (hazRes.status === 'fulfilled') setHazards(hazRes.value.data?.hazards || []);
      if (congRes.status === 'fulfilled') setCongestion(congRes.value.data?.congestion || []);
      if (sigRes.status === 'fulfilled') setSignals(sigRes.value.data?.signals || []);
      if (graphRes.status === 'fulfilled') setGraph(graphRes.value.data?.graph || null);
      if (calRes.status === 'fulfilled') setCalibration(calRes.value.data?.calibration || null);
      if (stateRes.status === 'fulfilled') setState(stateRes.value.data?.state || null);
      if (newsRes.status === 'fulfilled') setBriefs(newsRes.value.data?.briefs || []);
      if (predsRes.status === 'fulfilled') setWatchPredictions(predsRes.value.data?.predictions || []);
      if (notableRes.status === 'fulfilled') setNotable(notableRes.value.data?.notable || []);
      setFetchedAt(new Date().toISOString());
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const regime = state?.market_state || null;
  const regimeColor = REGIME_COLORS[regime] || '#9ca3af';

  const topEdges = (graph?.edges || [])
    .filter((e) => e.edge_status === 'active' && e.hazard_level !== 'high')
    .slice(0, 8);

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loading}><CircularProgress style={{ color: '#4d79ff' }} /></Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>Navigation Intelligence</Typography>
          <Typography className={classes.subtitle}>Capital flow signals from E3D Maps</Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          {regime && (
            <Box className={classes.regimeBadge} style={{ color: regimeColor, borderColor: regimeColor }}>
              {regime.replace('_', ' ')}
            </Box>
          )}
          {calibration && (
            <Box display="flex" style={{ gap: 20 }}>
              <Box className={classes.stat}>
                <Typography className={classes.statValue}>{(calibration.signals_last_24h || 0).toLocaleString()}</Typography>
                <Typography className={classes.statLabel}>signals / 24h</Typography>
              </Box>
              <Box className={classes.stat}>
                <Typography className={classes.statValue}>{fmtConf(calibration.avg_confidence)}</Typography>
                <Typography className={classes.statLabel}>avg confidence</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        className={classes.tabs}
        TabIndicatorProps={{ className: classes.tabIndicator }}
      >
        <Tab label="Traffic" className={classes.tab} />
        <Tab label="News" className={classes.tab} />
        <Tab label="Predictions" className={classes.tab} />
      </Tabs>

      {/* ── Traffic tab ──────────────────────────────────────── */}
      {tab === 0 && (
        <>
          <Box className={classes.grid}>
            <Paper className={classes.panel} elevation={0}>
              <Typography className={classes.panelTitle}>Top Destinations</Typography>
              {destinations.length === 0 ? (
                <Typography className={classes.empty}>No destination signals</Typography>
              ) : destinations.map((s) => (
                <Box key={s.id} className={classes.signalRow}>
                  <Typography className={classes.confidence}>{fmtConf(s.confidence)}</Typography>
                  <Box flex={1}>
                    <Typography className={classes.signalAnswer}>
                      {s.destination || s.answer || '—'}
                    </Typography>
                    {s.origin && (
                      <Typography className={classes.signalMeta}>
                        from {s.origin} · {timeAgo(s.created_at)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Paper>

            <Paper className={classes.panel} elevation={0}>
              <Typography className={classes.panelTitle}>Active Hazards</Typography>
              {hazards.length === 0 ? (
                <Typography className={classes.empty}>No active hazards</Typography>
              ) : hazards.map((s) => (
                <Box key={s.id} className={classes.hazardRow}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography className={classes.hazardLabel}>{s.origin || s.signal_type}</Typography>
                    <Typography style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}>{fmtConf(s.confidence)}</Typography>
                  </Box>
                  <Typography className={classes.hazardSub}>
                    {s.answer ? s.answer.slice(0, 100) : s.signal_type} · {timeAgo(s.created_at)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Box>

          <Box className={classes.grid}>
            <Paper className={classes.panel} elevation={0}>
              <Typography className={classes.panelTitle}>
                Capital Flow Graph
                {graph && (
                  <span style={{ fontWeight: 400, marginLeft: 8, textTransform: 'none', letterSpacing: 0, color: '#6b7280' }}>
                    {graph.node_count} nodes · {graph.edge_count} edges
                  </span>
                )}
              </Typography>
              {topEdges.length === 0 ? (
                <Typography className={classes.empty}>No flow graph data</Typography>
              ) : topEdges.map((e) => (
                <Box key={e.id} className={classes.flowRow}>
                  <Typography className={classes.flowOrigin}>{e.origin}</Typography>
                  <Typography className={classes.flowArrow}>→</Typography>
                  <Typography className={classes.flowDest}>{e.destination}</Typography>
                  <Typography className={classes.flowConf}>{fmtConf(e.confidence)}</Typography>
                </Box>
              ))}
            </Paper>

            <Paper className={classes.panel} elevation={0}>
              <Typography className={classes.panelTitle}>Congestion Zones</Typography>
              {congestion.length === 0 ? (
                <Typography className={classes.empty}>No congestion signals</Typography>
              ) : congestion.map((s) => (
                <Box key={s.id} className={classes.signalRow}>
                  <Box flex={1}>
                    <Typography className={classes.signalAnswer}>
                      {s.origin || s.destination || '—'}
                    </Typography>
                    <Typography className={classes.signalMeta}>
                      {s.answer ? s.answer.slice(0, 80) : ''} · {timeAgo(s.created_at)}
                    </Typography>
                  </Box>
                  <Typography className={classes.confidence}>{fmtConf(s.confidence)}</Typography>
                </Box>
              ))}
            </Paper>
          </Box>

          <Paper className={classes.fullPanel} elevation={0}>
            <Typography className={classes.panelTitle}>Recent Signals</Typography>
            {signals.length === 0 ? (
              <Typography className={classes.empty}>No signals</Typography>
            ) : signals.map((s) => (
              <Box key={s.id} className={classes.signalRow}>
                <Box
                  className={classes.signalBadge}
                  style={{
                    backgroundColor: (SIGNAL_TYPE_COLORS[s.signal_type] || '#4d79ff') + '22',
                    color: SIGNAL_TYPE_COLORS[s.signal_type] || '#4d79ff',
                    border: `1px solid ${(SIGNAL_TYPE_COLORS[s.signal_type] || '#4d79ff')}44`,
                  }}
                >
                  {SIGNAL_TYPE_LABELS[s.signal_type] || s.signal_type}
                </Box>
                <Box flex={1}>
                  <Typography className={classes.signalAnswer}>
                    {s.answer ? s.answer.slice(0, 120) : '—'}
                  </Typography>
                  {(s.origin || s.destination) && (
                    <Typography className={classes.signalMeta}>
                      {[s.origin, s.destination].filter(Boolean).join(' → ')} · {timeAgo(s.created_at)}
                    </Typography>
                  )}
                </Box>
                <Typography className={classes.confidence}>{fmtConf(s.confidence)}</Typography>
              </Box>
            ))}
          </Paper>
        </>
      )}

      {/* ── News tab ─────────────────────────────────────────── */}
      {tab === 1 && (
        <>
          {briefs.length === 0 ? (
            <Paper className={classes.fullPanel} elevation={0}>
              <Typography className={classes.empty}>No news briefs yet</Typography>
            </Paper>
          ) : briefs.map((b, i) => {
            const stanceColor = STANCE_COLORS[b.stance] || '#9ca3af';
            return (
              <Box key={i} className={classes.briefCard}>
                <Box className={classes.briefHeader}>
                  <Typography className={classes.briefHeadline}>{b.headline}</Typography>
                  {b.stance && (
                    <Box
                      className={classes.stanceBadge}
                      style={{ color: stanceColor, borderColor: stanceColor }}
                    >
                      {b.stance.replace('_', ' ')}
                    </Box>
                  )}
                </Box>
                <Typography className={classes.briefSummary}>{b.summary}</Typography>
                {b.tags && b.tags.length > 0 && (
                  <Box className={classes.briefTags}>
                    {b.tags.map((tag) => (
                      <Box key={tag} className={classes.briefTag}>{tag}</Box>
                    ))}
                  </Box>
                )}
                <Typography className={classes.briefTimestamp}>
                  {timeAgo(b.generated_at)}
                </Typography>
              </Box>
            );
          })}
        </>
      )}

      {/* ── Predictions tab ──────────────────────────────────── */}
      {tab === 2 && (
        <>
          {/* Notable signals */}
          <Paper className={classes.fullPanel} elevation={0}>
            <Typography className={classes.panelTitle}>Notable Signals</Typography>
            {notable.length === 0 ? (
              <Typography className={classes.empty}>No notable signals</Typography>
            ) : notable.map((s) => (
              <Box key={s.signal_id} className={classes.notableRow}>
                <Typography className={classes.notabilityScore}>{s.notability}</Typography>
                <Box flex={1}>
                  <Typography className={classes.signalAnswer}>
                    {s.summary ? s.summary.slice(0, 120) : '—'}
                  </Typography>
                  <Typography className={classes.signalMeta}>
                    <Box
                      component="span"
                      style={{
                        color: SIGNAL_TYPE_COLORS[s.signal_type] || '#4d79ff',
                        fontWeight: 700,
                        marginRight: 6,
                      }}
                    >
                      {SIGNAL_TYPE_LABELS[s.signal_type] || s.signal_type}
                    </Box>
                    {s.chain_scope && s.chain_scope.length > 0 && `${s.chain_scope.join(', ')} · `}
                    {timeAgo(s.created_at)}
                  </Typography>
                </Box>
                <Typography className={classes.confidence}>{fmtConf(s.confidence)}</Typography>
              </Box>
            ))}
          </Paper>

          {/* Watch predictions */}
          <Paper className={classes.fullPanel} elevation={0}>
            <Typography className={classes.panelTitle}>Watch Predictions</Typography>
            {watchPredictions.length === 0 ? (
              <Typography className={classes.empty}>No predictions yet</Typography>
            ) : watchPredictions.map((p) => {
              const statusColor = PREDICTION_STATUS_COLORS[p.status] || '#6b7280';
              const dirColor = DIRECTION_COLORS[p.realized_direction_expected] || '#9ca3af';
              return (
                <Box key={p.id} className={classes.predRow}>
                  <Typography className={classes.predClaim}>{p.claim}</Typography>
                  <Box className={classes.predMeta}>
                    <Box
                      className={classes.predBadge}
                      style={{ color: statusColor, borderColor: statusColor + '66' }}
                    >
                      {p.status}
                    </Box>
                    <Box
                      className={classes.predBadge}
                      style={{ color: dirColor, borderColor: dirColor + '66' }}
                    >
                      {p.realized_direction_expected} · {p.magnitude_expected}
                    </Box>
                    <Typography style={{ fontSize: 11, color: '#4d79ff', fontWeight: 700 }}>
                      {fmtPct(p.probability)} prob
                    </Typography>
                    <Typography style={{ fontSize: 11, color: '#6b7280' }}>
                      {p.evaluation_window_hours}h window
                    </Typography>
                    {p.asset_scope && p.asset_scope.length > 0 && (
                      <Typography style={{ fontSize: 11, color: '#9ea4d8' }}>
                        {p.asset_scope.join(', ')}
                      </Typography>
                    )}
                    <Typography style={{ fontSize: 11, color: '#6b7280' }}>
                      {timeAgo(p.created_at)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </>
      )}

      {fetchedAt && (
        <Typography className={classes.refreshNote}>
          Updated {timeAgo(fetchedAt)} · refreshes every 5 min
        </Typography>
      )}
    </Box>
  );
}
